import { useCallback, useEffect, useState } from 'react'
import {
  AlertCircle, ChevronDown, ChevronRight, ChevronLeft, Database,
  Download, KeyRound, Loader2, RefreshCw, Sigma, Terminal,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  adoptExternalTable, getLiveTableSample,
  type LiveColumnInfo, type LiveTableSampleResult, type SyncTarget,
} from '@/api/designers'
import ResultsGrid from './ResultsGrid'
import { rowsToCsv, downloadCsv } from './csv'

const PAGE_SIZES = [25, 50, 100, 200]

interface DataBrowserViewProps {
  project: string
  target: SyncTarget
  table: string
  /** Whether this table has a Database_Design entry. */
  inProject: boolean
  canEdit: boolean
  /** Seed the SQL console with a query against this table. */
  onOpenInQuery: (sql: string) => void
  /** Called after a successful adopt so the rail re-classifies the table. */
  onAdopted: () => void
}

/**
 * The Data tab — what is actually IN a live table.
 *
 * Deliberately shows structure and rows together: the question "why is this
 * column empty" is usually answered by its type/nullability, and flipping
 * between two places to see both is what makes a data browser tedious.
 */
export default function DataBrowserView({
  project, target, table, inProject, canEdit, onOpenInQuery, onAdopted,
}: DataBrowserViewProps) {
  const [data, setData] = useState<LiveTableSampleResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(25)
  const [offset, setOffset] = useState(0)
  const [exactCount, setExactCount] = useState(false)
  const [structureOpen, setStructureOpen] = useState(false)
  const [adopting, setAdopting] = useState(false)

  const load = useCallback(async (opts: { offset: number; limit: number; exact: boolean }) => {
    setLoading(true)
    try {
      const res = await getLiveTableSample(project, table, target, {
        limit: opts.limit, offset: opts.offset, exactCount: opts.exact,
      })
      setData(res)
    } catch (e) {
      setData({
        success: false,
        message: e instanceof Error ? e.message : 'Failed to read the table.',
      })
    } finally {
      setLoading(false)
    }
  }, [project, table, target])

  // Reset paging when the table or target changes — an offset from a previous
  // table is meaningless here.
  useEffect(() => {
    setOffset(0)
    setExactCount(false)
    void load({ offset: 0, limit: pageSize, exact: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, table, target])

  const goto = (newOffset: number, newSize = pageSize, exact = exactCount) => {
    setOffset(newOffset)
    setPageSize(newSize)
    setExactCount(exact)
    void load({ offset: newOffset, limit: newSize, exact })
  }

  const handleAdopt = async () => {
    setAdopting(true)
    try {
      const res = await adoptExternalTable(project, table, target)
      if (res.success) {
        toast.success(res.message || `${table} adopted into the project`)
        onAdopted()
      } else {
        toast.error(res.message || 'Could not adopt this table')
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not adopt this table')
    } finally {
      setAdopting(false)
    }
  }

  const handleExport = () => {
    if (!data?.sample_columns?.length) return
    downloadCsv(
      `${table}_rows.csv`,
      rowsToCsv(data.sample_columns, data.sample_rows || []),
    )
  }

  const columns: LiveColumnInfo[] = data?.columns || []
  const pkColumns = columns.filter((c) => c.pk).map((c) => c.column)
  const rowCount = data?.row_count
  const estimated = data?.row_count_estimated
  const failed = data && !data.success

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{
        padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--color-border)',
        flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
          <Database size={18} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <span style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--color-text)' }}>
            {table}
          </span>
          <span style={{
            fontSize: '0.8125rem', fontWeight: 600, padding: '1px 8px', borderRadius: 999,
            background: inProject ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
            color: inProject ? '#22c55e' : '#f59e0b',
          }}>
            {inProject ? 'In project' : 'Database only'}
          </span>
          {data?.schema && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              {data.schema} · {data.dialect}
            </span>
          )}

          <div style={{ flex: 1 }} />

          {!inProject && canEdit && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleAdopt}
              disabled={adopting}
              title="Create a Database_Design entry for this table (externally managed — deploy will never alter it)"
            >
              {adopting
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Download size={14} />}
              Adopt into project
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onOpenInQuery(`SELECT *\nFROM ${table}\nLIMIT 50`)}
            title="Open a query against this table in the SQL console"
          >
            <Terminal size={14} />
            Query
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => goto(offset)}
            disabled={loading}
            title="Reload rows"
            style={{ padding: '2px 6px' }}
          >
            <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          </button>
        </div>

        {/* Row count + paging */}
        {!failed && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          }}>
            <span>
              {rowCount === null || rowCount === undefined
                ? 'Row count unknown'
                : `${estimated ? '~' : ''}${rowCount.toLocaleString()} row${rowCount === 1 ? '' : 's'}`}
            </span>
            {estimated && (
              <button
                onClick={() => goto(offset, pageSize, true)}
                title="Run an exact COUNT(*) — may be slow on a large table"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--color-accent)', fontSize: '0.8125rem',
                }}
              >
                <Sigma size={11} /> count exactly
              </button>
            )}
            <span style={{ opacity: 0.5 }}>|</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Rows
              <select
                value={pageSize}
                onChange={(e) => goto(0, Number(e.target.value))}
                style={{ fontSize: '0.8125rem', padding: '1px 4px' }}
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => goto(Math.max(0, offset - pageSize))}
                disabled={offset === 0 || loading}
                style={{ padding: '1px 4px' }}
                title="Previous page"
              >
                <ChevronLeft size={13} />
              </button>
              <span>{offset + 1}–{offset + (data?.sample_rows?.length || 0)}</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => goto(offset + pageSize)}
                disabled={!data?.truncated || loading}
                style={{ padding: '1px 4px' }}
                title="Next page"
              >
                <ChevronRight size={13} />
              </button>
            </div>
            {!!data?.sample_columns?.length && (
              <button
                onClick={handleExport}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--color-accent)', fontSize: '0.8125rem',
                }}
                title="Download the rows on this page as CSV"
              >
                <Download size={11} /> CSV
              </button>
            )}
          </div>
        )}
      </div>

      {failed ? (
        <div style={{ padding: '1.25rem' }}>
          <div style={{
            display: 'flex', gap: '0.625rem', padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
          }}>
            <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)' }}>
                {data?.error === 'not_deployed'
                  ? 'This table is not in the live database'
                  : 'Could not read this table'}
              </div>
              <div style={{
                fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                marginTop: 4, fontFamily: 'var(--font-mono, monospace)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {data?.message}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Structure */}
          <div style={{ flexShrink: 0, borderBottom: '1px solid var(--color-border)' }}>
            <button
              onClick={() => setStructureOpen((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem', width: '100%',
                padding: '0.5rem 1.25rem', background: 'transparent', border: 'none',
                cursor: 'pointer', color: 'var(--color-text-secondary)',
                fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.04em', textAlign: 'left',
              }}
            >
              {structureOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              Structure ({columns.length} column{columns.length === 1 ? '' : 's'})
            </button>
            {structureOpen && (
              <div style={{ maxHeight: 220, overflow: 'auto', padding: '0 1.25rem 0.75rem' }}>
                <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--color-text-secondary)', textAlign: 'left' }}>
                      <th style={{ padding: '4px 8px', fontWeight: 600 }}>Column</th>
                      <th style={{ padding: '4px 8px', fontWeight: 600 }}>Type</th>
                      <th style={{ padding: '4px 8px', fontWeight: 600 }}>Null</th>
                      <th style={{ padding: '4px 8px', fontWeight: 600 }}>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map((c) => (
                      <tr key={c.column} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '4px 8px', color: 'var(--color-text)' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {c.pk && <KeyRound size={11} style={{ color: '#f59e0b' }} />}
                            {c.column}
                          </span>
                        </td>
                        <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono, monospace)' }}>
                          {c.type}
                        </td>
                        <td style={{ padding: '4px 8px' }}>{c.nullable ? 'yes' : 'no'}</td>
                        <td style={{
                          padding: '4px 8px', maxWidth: 220, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={c.default || ''}>
                          {c.default || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Rows */}
          <div style={{ flex: 1, minHeight: 0, padding: '0.5rem 0.75rem 0.75rem' }}>
            {loading && !data ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
              </div>
            ) : (
              <ResultsGrid
                columns={data?.sample_columns || []}
                rows={data?.sample_rows || []}
                pkColumns={pkColumns}
                emptyMessage="This table has no rows"
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
